// Card #1652 (design §6.1) — Ranked list family, mode types 6 (Mid-Class
// Result) and 7 (End of Class Result): "class header + ranked rows +
// banner." `rankMode` is the wording gate (design §4.5): type 6 is NOT
// end-of-class yet, so its rows say "Rank"; type 7 is the one case §4.5
// permits "place"/"placing" wording, so its rows say "Place". (Card #1794's
// pare-down removed type 6 entirely — End of Class Result, type 7, is now
// this family's only surviving caller — but the component stays mode-
// agnostic rather than hardcoding a wording it no longer varies on.)
//
// Card #1791 — a class can run past what one column holds at projector
// legibility (WEC 341848 class 215: 19 rides, a NORMAL size, not an
// outlier). Rows split into fixed groups of 10, side by side (Jaybill,
// 2026-08-20: "two columns on one screen, NOT a rotating/paginated
// carousel"), reading top-to-bottom then left-to-right (AC2): column N
// holds rows [10N, 10N+10). A class of 10 or fewer stays a single column
// (AC3) because it only ever produces one chunk, so no wrapper and no
// half-empty second column exist for it to render.
//
// Past 20 rows is the card's own "open, needs a decision during the work."
// Decision made here: keep chunking in groups of 10 rather than capping at
// two columns or introducing scrolling — a 21st+ rider gets a third column
// instead of being silently dropped (AC4). The board is an unattended,
// public display with no error affordance, so a wider-than-usual layout is
// the safer failure mode over a truncated ranked list.
import { useEffect, useState } from "react";
import ClassHeader from "./ClassHeader";
import RankedList, { computeIdentityColumns, computeShowScoreColumn } from "./RankedList";
import Banner from "./Banner";
import { RESULT_LAYOUTS, resolveResultLayout, resolveResultDwellSeconds } from "score-common/constants";
import "./RankedListLayout.css";

const GROUP_SIZE = 10;

function chunkRankedList(rankedList) {
  if (!Array.isArray(rankedList) || !rankedList.length) return [];
  const chunks = [];
  for (let start = 0; start < rankedList.length; start += GROUP_SIZE) {
    chunks.push(rankedList.slice(start, start + GROUP_SIZE));
  }
  return chunks;
}

// Card #1945 — FVD has no literal "division" field; docs/score/fvd-data-
// model.md §1 (#756, RESOLVED) is "division" == rider status: class-level
// Open/AA/JR-YR/Other eligibility bit-flags, foxbox-derived onto each
// entry as `entryIdentity.riderStatus`. That doc entry describes the
// CLASS-level `tblClass` derivation; the value actually grouped on here is
// per-RIDER (`tblRider.chrStatus`), whose authority is
// apps/score-api/src/handlers/ride.js:447-452 (#821/#784, which is
// explicit that "Class.division... is a per-CLASS eligibility derivation,
// not per-rider" — the two are synonymous in practice but this grouping
// reads the per-rider field, not the class-level one). score-api's
// composeRankedList already carries that per row (composeEntryIdentity) —
// grouping it here is a pure FE presentation concern, no new payload key,
// no score-api change.
//
// Case-insensitive (trim + lowercase) because the SAME FVD class spells one
// division two ways in real data (`Jr/YR` vs `JR/YR`, WEC class 119) and
// that must collapse to ONE table (AC3), never split on casing alone. The
// FIRST-seen original casing is kept as the display name — a WEC director
// reads whichever spelling foxbox happened to write first, not a re-cased
// label manufactured here.
export function divisionKeyOf(row) {
  const status = row?.entryIdentity?.riderStatus;
  return typeof status === "string" && status.trim() ? status.trim().toLowerCase() : null;
}

// Card #2027 (Jaybill, 2026-08-30): a row with no riderStatus displays as
// "Open" and ranks inside the Open division, so the board NEVER renders an
// unlabelled table. A blank `tblRider.chrStatus` (riderStatusOf maps it to
// undefined, ride.js) and a manual/emergency ride with no riderId (card 386)
// are both EXPECTED shapes, not defensive ones: they are what real data looks
// like in a class that otherwise carries divisions.
//
// The null-to-Open mapping applies ONLY when the class already carries at
// least one real division. A class where nobody has a division is not a
// one-division class: it keeps `key: null` for every row, which the caller
// reads as "no division data" (hasDivisions false), so it renders today's
// single merged list with no heading at all, never one headed Open.
const OPEN_DIVISION_NAME = "Open";
const OPEN_DIVISION_KEY = OPEN_DIVISION_NAME.toLowerCase();

export function groupRankedListByDivision(rankedList) {
  if (!Array.isArray(rankedList)) return [];
  const classHasDivisions = rankedList.some((row) => divisionKeyOf(row) != null);
  const groups = [];
  const indexByKey = new Map();
  for (const row of rankedList) {
    let key = divisionKeyOf(row);
    let name = key == null ? null : row.entryIdentity.riderStatus.trim();
    if (key == null && classHasDivisions) {
      key = OPEN_DIVISION_KEY;
      name = OPEN_DIVISION_NAME;
    }
    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, name, rows: [] });
    }
    groups[indexByKey.get(key)].rows.push(row);
  }
  return groups;
}

// Card #3108 -- Mid-Class Result (rankMode "rank") hands down the CLASS-wide
// composite rank (composeMidClassResult: rank = index + 1 of one sort across
// every division). That is right for the COMBINED layout, but a by-division
// page shows ONE division, so an Adult Amateur page could read 2, 5, 7 while
// that division's own standings are 1, 2, 3. The payload is shared by both
// layouts and the layout is resolved here, so the FE renumbers 1..n inside
// each division group, in the group's own (already rank-ordered) row order.
// A terminated row carries no rank (#2484), so it stays unranked and does not
// consume a number. End of Class Result (rankMode "placing") never comes
// through here: it shows FVD's stored per-division placing untouched.
export function renumberDivisionRanks(groups) {
  return groups.map((group) => {
    let next = 1;
    return {
      ...group,
      rows: group.rows.map((row) => {
        const rank = row?.rank;
        if (!(typeof rank === "number" && Number.isFinite(rank) && rank > 0)) return row;
        return { ...row, rank: next++ };
      }),
    };
  });
}

// Card #2439: the empty-rankedList case never reaches this component. It is
// decided upstream by classifyBoardRenderState (boardRenderState.js,
// RANKED_LIST_EMPTY), which routes the board to BlankLayout. This layout's
// per-row absent-cell rule (#1780) therefore only ever sees a non-empty list.

// Card #2332 — by-division now means a looping slideshow: ONE division on
// screen at a time (full width, the same single-division shape the approved
// demo shows), advancing every `dwellSeconds` and wrapping back to the first.
// Replaces #1945's all-divisions-at-once sibling tables; the stored
// `resultLayout` value is unchanged (DECIDED BY JAYBILL 2026-09-05).
//
// The board polls every 1s (ScoreboardPage POLL_INTERVAL_MS) and each poll
// hands down a fresh `groups` array, so the timer MUST NOT be keyed off
// object identity or it would restart every second and freeze on division 1
// forever. Its effect deps are primitives only: the division COUNT, the dwell
// in ms, and a stable signature of the ordered division keys plus the class
// number. `groups` itself is read at render time, never from the effect, so
// live row updates inside a division show immediately without touching the
// timer. A changed signature (different class, or a division appearing or
// disappearing) restarts from division 1 by design.
//
// A class with exactly ONE division has nothing to rotate: no timer is ever
// created, so it cannot flicker. `index % count` at render keeps a stale
// index in range if the division count shrinks, so the board can never
// render an undefined slide (doctrine #1798: never errors, never blanks).
export function DivisionSlideshow({ groups, dwellSeconds, resetKey, renderScope }) {
  const count = groups.length;
  const signature = `${resetKey}|${groups.map((group) => group.key ?? "__none").join("|")}`;
  const [slide, setSlide] = useState({ signature, index: 0 });
  // Derived reset: a new class / division set starts again at division 1
  // without a flash of the old index (no effect round trip needed).
  const index = slide.signature === signature ? slide.index % Math.max(count, 1) : 0;
  const dwellMs = dwellSeconds * 1000;

  useEffect(() => {
    if (count < 2) return undefined;
    const timer = setInterval(() => {
      setSlide((prev) => {
        const base = prev.signature === signature ? prev.index : 0;
        return { signature, index: (base + 1) % count };
      });
    }, dwellMs);
    return () => clearInterval(timer);
  }, [signature, count, dwellMs]);

  const group = groups[index];
  return (
    <div
      className="scoreboard-ranked-list__divisions"
      data-testid="scoreboard-ranked-list-divisions"
      data-division-index={index}
      data-division-count={count}
    >
      <div
        key={group.key ?? "__none"}
        className="scoreboard-ranked-list__division"
        data-testid="scoreboard-ranked-list-division"
      >
        {group.name && (
          <div className="scoreboard-ranked-list__division-heading" data-testid="scoreboard-ranked-list-division-heading">
            <span className="scoreboard-ranked-list__division-label">DIVISION</span>
            <span className="scoreboard-ranked-list__division-name" data-testid="scoreboard-ranked-list-division-name">
              {group.name}
            </span>
          </div>
        )}
        {renderScope(group.rows)}
      </div>
    </div>
  );
}

export default function RankedListLayout({ fields, banner, rankMode }) {
  // #1835: same silent-failure shape SingleEntryLayout fixed under #2002 AC5.
  // A bare `return null` left an empty `.scoreboard-content` (no logo,
  // nothing for a boundary to catch) if a caller ever skipped
  // classifyBoardRenderState. Throw so the ScoreboardErrorBoundary around
  // every layout family falls back to Blank + logo instead. Unreachable via
  // ScoreboardContent today (the classifier routes a missing `fields` to
  // Blank first): belt-and-braces against a broken caller.
  if (!fields) throw new Error("RankedListLayout rendered without fields");

  const rankedList = fields.rankedList;
  // Column headings (#1780, AC5) and the Score column's own #1780 AC5
  // presence rule (#1944) must agree across every sibling table — computed
  // ONCE against the FULL class list and passed into every table, exactly
  // #1791's "compute once, apply to every chunk" rule for the groups-of-10
  // split. Design decision (card #1945): this now also spans every
  // DIVISION's table, not just chunk-columns within one division. Divisions
  // rendered side by side are still siblings of ONE class result — letting
  // one division show a Horse column the other doesn't would read as a data
  // inconsistency rather than the coincidence it actually is.
  const resultLayout = resolveResultLayout(fields.resultLayout);
  const groups = groupRankedListByDivision(rankedList);
  const hasDivisions = resultLayout !== RESULT_LAYOUTS.COMBINED && groups.some((group) => group.key != null);
  // Card #2484 -- Mid-Class Result (rankMode "rank", AC1's "rider
  // status-division", the mockup's Status/Div column) keeps the Status column
  // wherever no DIVISION heading already carries it. A by-division board
  // (#2027) and the final-results board (rankMode "placing") are unchanged.
  const columns = computeIdentityColumns(rankedList, { includeStatus: rankMode === "rank" && !hasDivisions });
  const showScore = computeShowScoreColumn(rankedList);

  function renderScope(rows) {
    const chunks = chunkRankedList(rows);
    if (chunks.length > 1) {
      return (
        <div className="scoreboard-ranked-list__columns" data-testid="scoreboard-ranked-list-columns">
          {chunks.map((chunk, index) => (
            <RankedList key={index} rankedList={chunk} rankMode={rankMode} columns={columns} showScore={showScore} />
          ))}
        </div>
      );
    }
    return <RankedList rankedList={rows} rankMode={rankMode} columns={columns} showScore={showScore} />;
  }

  // AC2 — a class where NO row carries a division value renders exactly as
  // before: one scope, no `__divisions` wrapper, no DIVISION heading at all.
  // Design decision (card #1945, the #1780 "decide scope deliberately"
  // note): the gate is "does this class carry ANY division data", not "are
  // there 2+ distinct divisions" — a class with exactly one division value
  // on every row (AC6's "1-division class") still HAS division data, so it
  // still renders under a DIVISION heading, just as a single table with
  // nothing beside it to disambiguate from.
  // Card #2314 — the operator's per-class layout choice (AC1/AC3).
  // score-api already resolves an unset/unrecognised stored value to
  // BY_DIVISION server-side (resolveResultLayout in composeEndOfClassResult),
  // but this reader applies the SAME fails-closed resolver again rather than
  // trusting `fields.resultLayout` verbatim — the scoreboard doctrine (#1798)
  // is that the board itself never errors or blanks on a bad value, so a
  // stale/mocked/malformed payload degrades the exact same way here as it
  // does upstream (AC4), belt-and-braces. COMBINED skips the division
  // grouping entirely and renders the whole class as one scope, exactly like
  // the "no division data at all" case below already does.

  const rankedListBody = hasDivisions ? (
    <DivisionSlideshow
      groups={rankMode === "rank" ? renumberDivisionRanks(groups) : groups}
      dwellSeconds={resolveResultDwellSeconds(fields.resultDwellSeconds)}
      resetKey={String(fields.classNumberName?.classNumber ?? "")}
      renderScope={renderScope}
    />
  ) : (
    renderScope(rankedList)
  );

  // Card #1949 (parity with the approved demo pages, card #1928): the
  // banner leads the layout instead of closing it. FINAL CLASS RESULTS is
  // the first thing read at distance, above the class number/name, not a
  // trailing footnote under the table.
  return (
    <div className="scoreboard-ranked-list" data-testid="scoreboard-ranked-list">
      <Banner banner={banner} />
      <ClassHeader classNumberName={fields.classNumberName} judgesPositions={fields.judgesPositions} />
      {rankedListBody}
    </div>
  );
}
